import React, { useMemo } from 'react'
import { pluralizeToString } from 'lib/string/pluralize'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'

import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import Group from 'components/utils/Group'
import Pill from 'components/Luxkit/Pill'
import PriceRowPriceWithCaption from 'components/Luxkit/PricePoints/PriceRowPriceWithCaption'

interface Props {
  selectedDay: App.CalendarDay;
  offer: App.Offer;
  isAnyFlightsActive?: boolean;
  duration: number;
  isFromPrice?: boolean;
}

function OfferCalenderDatePickerFooterPriceDetails(props: Props) {
  const { selectedDay, offer, isAnyFlightsActive, duration, isFromPrice } = props

  const canShowPrice = !(offer.bundledWithFlightsOnly && !selectedDay?.flightCacheActive)
  const [price, value, discount, taxesAndFees] = useMemo(() => {
    if (selectedDay) {
      const discount = Math.round((selectedDay.hotelValue - selectedDay.hotelPrice) * 100 / selectedDay.hotelValue)
      if (isAnyFlightsActive) {
        return [selectedDay.packagedTotal, selectedDay.hotelValue + selectedDay.flightPrice, discount, selectedDay.taxesAndFees]
      }
      return [selectedDay.hotelTotal, selectedDay.hotelValue, discount, selectedDay.taxesAndFees]
    }
    return [0, 0, 0, 0]
  }, [isAnyFlightsActive, selectedDay])

  const shouldShowDiscountPercent = discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const durationCaption = pluralizeToString('night', duration)

  if (!canShowPrice) {
    return <Caption variant="large">
      {durationCaption}
    </Caption>
  }

  return <div>
    <PriceRowPriceWithCaption
      size="M"
      caption={`${durationCaption} ${isFromPrice ? 'from' : ''}`}
      price={price}
      saleUnit={`total${isAnyFlightsActive ? '*' : ''}`}
      taxesAndFees={taxesAndFees}
    />
    <PriceRowTaxesAndFees
      isTaxExclusiveRegion={offer.property?.taxesPayableAtProperty}
      taxesAndFees={selectedDay.taxesAndFees}
    />
    {value > price && offer.shouldDisplayValue && <Group direction="horizontal" verticalAlign="center" gap={4}>
      <Caption variant="medium" weight="bold">
        Valued up to <FormatCurrency value={value} />
      </Caption>
      {!isAnyFlightsActive && shouldShowDiscountPercent && <Pill kind="primary" variant="success">
        -{discount}%
      </Pill>}
    </Group>}
  </div>
}

export default OfferCalenderDatePickerFooterPriceDetails
